import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  Image, Video, Upload, X, Trash2, Flag, Loader2, 
  ChevronLeft, ChevronRight, Play, Pause, ZoomIn
} from "lucide-react";

const EventGallery = ({ eventId, token, isAdmin = false }) => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [fullMediaData, setFullMediaData] = useState(null);
  const [caption, setCaption] = useState("");
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchGallery();
    }
  }, [eventId]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/gallery`);
      setGallery(response.data.gallery || []);
    } catch (err) {
      console.error("Error fetching gallery:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Determine media type
    let mediaType = "image";
    if (file.type === "image/gif") {
      mediaType = "gif";
    } else if (file.type.startsWith("video/")) {
      mediaType = "video";
    }

    // Validate file size
    const maxSize = mediaType === "video" ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max size: ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      await uploadMedia(mediaType, base64);
    };
    reader.readAsDataURL(file);
  };

  const uploadMedia = async (mediaType, mediaData) => {
    if (!token) {
      toast.error("Please log in to upload media");
      return;
    }

    setUploading(true);
    try {
      await axios.post(
        `${API}/events/${eventId}/gallery`,
        {
          media_type: mediaType,
          media_data: mediaData,
          caption: caption
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Media uploaded successfully!");
      setCaption("");
      fetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload media");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (mediaId) => {
    if (!confirm("Are you sure you want to delete this media?")) return;

    try {
      await axios.delete(`${API}/events/${eventId}/gallery/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Media deleted");
      fetchGallery();
      setSelectedMedia(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete media");
    }
  };

  const handleFlag = async (mediaId) => {
    try {
      await axios.post(
        `${API}/events/${eventId}/gallery/${mediaId}/flag`,
        { reason: "inappropriate" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Media flagged for review");
      fetchGallery();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to flag media");
    }
  };

  const openMediaViewer = async (media) => {
    setSelectedMedia(media);
    // Fetch full media data if truncated
    if (!media.media_data || media.media_data.endsWith("...")) {
      try {
        const response = await axios.get(`${API}/events/${eventId}/gallery/${media.id}`);
        setFullMediaData(response.data.media_data);
      } catch (err) {
        console.error("Error fetching full media:", err);
      }
    } else {
      setFullMediaData(media.media_data);
    }
  };

  const navigateMedia = (direction) => {
    const currentIndex = gallery.findIndex(m => m.id === selectedMedia?.id);
    let newIndex;
    
    if (direction === "next") {
      newIndex = currentIndex < gallery.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : gallery.length - 1;
    }
    
    openMediaViewer(gallery[newIndex]);
  };

  const toggleVideoPlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const getMediaIcon = (mediaType) => {
    switch (mediaType) {
      case "video":
        return <Video className="w-4 h-4" />;
      case "gif":
        return <Play className="w-4 h-4" />;
      default:
        return <Image className="w-4 h-4" />;
    }
  };

  return (
    <div data-testid="event-gallery">
      {/* Upload Section */}
      {token && (
        <Card className="bg-[#0A0A0A] border border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption (optional)"
                className="input-dark flex-1"
                data-testid="gallery-caption-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,video/webm,.gif"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="gallery-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="btn-gold"
                data-testid="gallery-upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploading ? "Uploading..." : "Upload Photo/Video"}
              </Button>
            </div>
            <p className="text-[#52525B] text-xs mt-2 font-ubuntu">
              Supported: Images (PNG, JPG, WebP), GIFs, Videos (MP4, WebM) • Max: 5MB images, 50MB videos
            </p>
          </CardContent>
        </Card>
      )}

      {/* Gallery Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#7a6200] animate-spin" />
        </div>
      ) : gallery.length === 0 ? (
        <div className="text-center py-12 text-[#52525B]">
          <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="font-ubuntu text-lg">No photos or videos yet</p>
          <p className="font-ubuntu text-sm mt-2">Be the first to share a moment from this event!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {gallery.map((media) => (
            <div
              key={media.id}
              className="relative group aspect-square bg-[#0A0A0A] rounded-lg overflow-hidden border border-white/10 cursor-pointer hover:border-[#7a6200]/50 transition-colors"
              onClick={() => openMediaViewer(media)}
              data-testid={`gallery-item-${media.id}`}
            >
              {/* Thumbnail */}
              {media.media_type === "video" ? (
                <div className="w-full h-full flex items-center justify-center bg-black">
                  <Play className="w-12 h-12 text-white/50" />
                </div>
              ) : (
                <img
                  src={media.thumbnail || media.media_data}
                  alt={media.caption || "Gallery item"}
                  className="w-full h-full object-cover"
                />
              )}
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <ZoomIn className="w-8 h-8 text-white" />
              </div>

              {/* Media type badge */}
              <div className="absolute top-2 right-2 bg-black/70 rounded px-2 py-1 flex items-center gap-1">
                {getMediaIcon(media.media_type)}
              </div>

              {/* Flagged indicator */}
              {media.status === "flagged" && (
                <div className="absolute top-2 left-2 bg-yellow-500/80 text-black rounded px-2 py-1 text-xs font-ubuntu">
                  Flagged
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Media Viewer Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
          {/* Close button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedMedia(null);
              setFullMediaData(null);
              setIsPlaying(false);
            }}
            className="absolute top-4 right-4 text-white hover:bg-white/10 z-10"
            data-testid="close-media-viewer"
          >
            <X className="w-6 h-6" />
          </Button>

          {/* Navigation arrows */}
          {gallery.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateMedia("prev")}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
              >
                <ChevronLeft className="w-8 h-8" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigateMedia("next")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
              >
                <ChevronRight className="w-8 h-8" />
              </Button>
            </>
          )}

          {/* Media content */}
          <div className="max-w-4xl max-h-[80vh] p-4">
            {!fullMediaData ? (
              <Loader2 className="w-12 h-12 text-[#7a6200] animate-spin" />
            ) : selectedMedia.media_type === "video" ? (
              <div className="relative">
                <video
                  ref={videoRef}
                  src={fullMediaData}
                  className="max-w-full max-h-[70vh] rounded"
                  controls
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
              </div>
            ) : (
              <img
                src={fullMediaData}
                alt={selectedMedia.caption || "Gallery item"}
                className="max-w-full max-h-[70vh] rounded object-contain"
              />
            )}

            {/* Caption and info */}
            <div className="mt-4 text-center">
              {selectedMedia.caption && (
                <p className="text-white font-ubuntu mb-2">{selectedMedia.caption}</p>
              )}
              <p className="text-[#52525B] text-sm font-ubuntu">
                Uploaded by {selectedMedia.uploader_name} • {new Date(selectedMedia.created_at).toLocaleDateString()}
              </p>
            </div>

            {/* Actions */}
            <div className="mt-4 flex items-center justify-center gap-4">
              {token && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFlag(selectedMedia.id)}
                    className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                    data-testid="flag-media-btn"
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    Report
                  </Button>
                  {(isAdmin || selectedMedia.uploader_id === "current") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(selectedMedia.id)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid="delete-media-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventGallery;
